import { forwardRef, type InputHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  hint?: string;
  error?: string;
  suffix?: string;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, hint, error, suffix, id, ...props }, ref) => {
    const inputId = id ?? props.name;
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-[11px] uppercase tracking-[0.16em] text-ink-dim font-semibold"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <input
            id={inputId}
            ref={ref}
            className={cn(
              "w-full rounded-xl border bg-bg-raised px-3.5 py-2.5 text-sm",
              "text-ink placeholder:text-ink-dim",
              "transition-colors focus:outline-none focus:ring-2 focus:ring-amber/50 focus:border-amber/50",
              error
                ? "border-loss/50 focus:border-loss focus:ring-loss/30"
                : "border-stroke",
              suffix && "pr-12",
              className,
            )}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : undefined}
            {...props}
          />
          {suffix && (
            <span className="mono absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-dim">
              {suffix}
            </span>
          )}
        </div>
        {error ? (
          <p id={`${inputId}-error`} className="text-xs text-loss">
            {error}
          </p>
        ) : hint ? (
          <p className="text-xs text-ink-dim">{hint}</p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";
